import { memo } from "react";
import { useSelector } from "react-redux";

import { McpAppFrame, useInAppLinks } from "../aui/McpAppFrame";
import { nativeChoiceCard } from "../cards";
import type { AgentState } from "../store/agentSlice";
import { selectChoice } from "../store/selectors";
import { THREAD_CARD } from "./cardWidth";
import { PositionApprovalCard } from "./PositionApprovalCard";
import { ReclassifyApprovalCard } from "./ReclassifyApprovalCard";
import { choiceSubject, positionCard, transactionCard } from "./wire";

export { CARD_WIDTH, THREAD_CARD } from "./cardWidth";

/**
 * An approval in the thread, paid checks included: V's approval view in our sandboxed frame.
 * The view's own card (our tokens, 12px radius) is the card, so nothing wraps it but its width.
 * A choice whose tool card has a native card (`NATIVE_CARDS`, by the card's kind) draws as that
 * card instead: an approval no tool part holds (a follow-up, an app's approval) included.
 * One whose rows are transactions or a position's fields is our own card instead: it reuses the
 * AI Analysis tab's pieces and asks what the ticked rows would change, which the sandboxed view
 * can't.
 */
export const ApprovalCard = memo(function ApprovalCard({ choiceRef }: { choiceRef: string }) {
  const prompt = useSelector(
    (state: { agent: AgentState }) => selectChoice(state, choiceRef)?.prompt
  );
  const subjectRef = useSelector((state: { agent: AgentState }) => {
    const choice = selectChoice(state, choiceRef);
    return (choice && choiceSubject(choice)?.ref) ?? null;
  });
  const openLink = useInAppLinks(subjectRef);
  const Native = useSelector((state: { agent: AgentState }) =>
    nativeChoiceCard(selectChoice(state, choiceRef))
  );
  const own = useSelector((state: { agent: AgentState }) => {
    const choice = selectChoice(state, choiceRef);
    if (!choice) return null;
    if (transactionCard(choice)) return "transactions";
    return positionCard(choice) ? "positions" : null;
  });
  if (Native) return <Native choiceRef={choiceRef} />;
  if (own === "transactions") return <ReclassifyApprovalCard choiceRef={choiceRef} />;
  if (own === "positions") return <PositionApprovalCard choiceRef={choiceRef} />;
  return (
    <div className={THREAD_CARD} data-choice-ref={choiceRef}>
      {/* Each frame is named for what it asks, so two approvals in a thread can be told apart. */}
      <McpAppFrame
        choiceRef={choiceRef}
        title={prompt ? `Approval: ${prompt}` : undefined}
        openLink={openLink}
      />
    </div>
  );
});
